import React, { useState, useEffect } from 'react';
import { Link, useStaticQuery, graphql, navigate } from 'gatsby';
import styled, { css, useTheme } from 'styled-components';
import { useMediaQuery } from '../hooks/useMediaQuery';
import CloseIcon from './Icons/Close';
import ChevronDownIcon from './Icons/ChevronDown';
import SafeSiteImage from './SafeSiteImage';
import { HtmlContent } from './Content';
import { globalHistory } from "@reach/router";
import { convertToRelativePath } from '../utils/convertToRelativePath';

const MegaMenuStyles = styled.div`
  ${({ theme, $withTertiary }) => css`
    position: absolute;
    left: 0;
    top: calc(100% + 1px);
    width: 100%;
    background: #fff;
    z-index: 9999;
    padding: 24px;
    box-shadow: -1px 5px 12px -9px #474747;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;

    ${$withTertiary &&
    css`
      display: grid;
      grid-template-columns: 1.66fr repeat(3, 1fr);
      gap: 24px;

      @media (max-width: 991px) {
        display: block;
      }
    `}

    @media (max-width: 991px) {
      padding-top: 24px;
      margin-top: 8px;
      border-top: 1px solid ${theme.colors.darkGrey};
      position: relative;
      box-shadow: none;
      padding: 0;
    }

    @media (max-width: 575px) {
      padding-top: 16px;
    }
  `}
`;

const Column = styled.div`
  ${({ $span }) =>
    $span &&
    css`
      grid-column: span ${$span > 1 ? $span : '1'};
    `}
`;

const SecondaryList = styled.ul`
  ${({ theme, $withTertiary }) =>
    $withTertiary
      ? css`
          display: flex;
          flex-direction: column;
          gap: 12px;
          border-right: 2px solid ${theme.colors.lightGrey};
          padding-right: 24px;

          @media (max-width: 991px) {
            border-right: none;
            padding-right: 0;
            gap: 0;

            > li {
              display: flex;
              flex-direction: column;
              gap: 16px;

              &:has(button[aria-expanded="true"]) {
                margin-bottom: 20px;
              }
            }
          }
        `
      : css`
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;

          @media (min-width: ${theme.breakpoints.lg}) {
            grid-template-columns: repeat(4, 1fr);
          }
        `}
`;

const SecondaryItem = styled.li`
  ${({ theme }) => css`
    list-style-type: none;
    width: 100%;

    h2,
    .secondary-link-content {
      font-size: 0.875rem;
      line-height: initial;
      font-weight: 400;
      text-transform: none;
    }

    h2 {
      color: ${theme.colors.primary};
    }

    .secondary-link-content {
      margin-top: 4px;
      color: ${theme.colors.darkGrey};
    }

    .secondary-image-wrapper {
      border-radius: 4px;
      margin-bottom: 20px;
      overflow: hidden;
    }

    img {
      transition: transform 0.5s ease-in;
      display: block;
    }

    a {
      display: block;
      transition: background 0.3s ease-out;
      padding: 12px 16px;
      border-radius: 4px;
    }

    a:hover {
      background: ${theme.colors.lightGrey};
      color: ${theme.colors.primary};

      img {
        transform: scale(1.1)
      }
    }

    @media (max-width: 991px) {
      .secondary-image-wrapper {
        display: none;
      }
    }
  `}
`;

const SecondaryItemButton = styled.button`
  ${({ theme, $active }) => css`
    background: ${$active ? theme.colors.lightGrey : 'none'};
    border: none;
    text-align: left;
    font-weight: 400;
    text-transform: none;
    color: ${theme.colors.primary};
    cursor: pointer;
    line-height: initial;
    font-size: 0.875rem;
    padding: 12px 16px;
    display: grid;
    grid-template-columns: 27px 1fr auto;
    align-items: center;
    gap: 12px;
    width: 100%;

    img {
      width: 27px;
      height: 27px;
    }

    svg {
      transform: rotate(-90deg);
    }

    .plus-icon {
      font-size: 2rem;
      line-height: 1;
    }

    &:hover {
      background: ${theme.colors.lightGrey};
      color: ${theme.colors.primary};
    }
  `}
`;

const TertiaryList = styled.ul`
  ${({ theme, $span }) => css`
    display: grid;
    column-gap: 24px;
    row-gap: 16px;
    grid-template-columns: repeat(${$span}, 1fr);

    @media (max-width: 991px) {
      grid-template-columns: 1fr;
      padding: 0 16px;
    }

    @media (min-width: ${theme.breakpoints.lg}) {
      grid-auto-flow: column;
      grid-template-rows: repeat(11, auto);
    }
  `}
`;

const TertiaryItem = styled.li`
  ${({ theme }) => css`
    list-style: none;

    a {
      padding: 0;
      text-transform: none;
      font-weight: 400;
      text-align: left;
      letter-spacing: 0;

      &:hover {
        color: ${theme.colors.primary};
        text-decoration: underline;
      }
    }
  `}
`;

const TertiaryHeadingItem = styled(TertiaryItem)`
  grid-column: 1/-1;

  a {
    font-weight: 600;
    font-size: 1rem;
  }
`;

const sharedImageStyles = css`
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: flex-end;
  padding: 20px;
  background-color: #000;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  h2 {
    font-size: 1.66rem;
    line-height: 1.2;
    z-index: 1;
    color: #fff;
    text-shadow: 0px 0px 40px rgba(0,0,0,0.9)
  }

  @media (max-width: 991px) {
    display: none;
  }
`;

const StyledImageLink = styled(Link)`
  ${sharedImageStyles}

  img {
    opacity: 0.75;
    transition: opacity 0.3s ease-out, transform 0.5s ease-in;
  }

  &:hover img {
    opacity: 0.66;
    transform: scale(1.1);
  }
`;

const StyledImageSpan = styled.span`
  ${sharedImageStyles}
`;

const CloseButton = styled.button`
  ${({ theme }) => css`
    background: #fff;
    color: ${theme.colors.darkGrey};
    border: none;
    position: absolute;
    bottom: -80px;
    left: 50%;
    transform: translateX(-50%);
    height: 40px;
    width: 40px;
    cursor: pointer;
    border-radius: 50%;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background-color: ${theme.colors.primary};
      color: #fff;
    }

    @media (max-width: 991px) {
      display: none;
    }
  `}
`;

const MegaMenu = ({ item, onClose }) => {
  if (!item || !item.children || item.children.length === 0) {
    return null;
  }

  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);
  const siteUrl = site.siteMetadata.siteUrl;

  const hasTertiary = item.children.some(
    (child) => child.children && child.children.length > 0,
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints.lg})`,
  );

  const [activeSecondary, setActiveSecondary] = useState(null);
  const [currentPath, setCurrentPath] = useState(() =>
    convertToRelativePath(globalHistory.location?.pathname || ''),
  );

  useEffect(() => {
    return globalHistory.listen(({ location }) => {
      setCurrentPath(convertToRelativePath(location.pathname));
    });
  }, []);

  useEffect(() => {
    if (hasTertiary && item?.children?.length > 0) {
      const match = item.children.find((child) => {
        const secondaryPath = convertToRelativePath(child.path);

        if (currentPath === secondaryPath || currentPath.startsWith(`${secondaryPath}/`)) {
          return true;
        }

        return child.children?.some((ter) => {
          const terPath = convertToRelativePath(ter.path);

          if (currentPath === terPath || currentPath.startsWith(`${terPath}/`)) {
            return true;
          }
        });
      });

      if (match) {
        setActiveSecondary(match)

        return
      }

      setActiveSecondary(!isMobile ? item.children[0] : null);
    }
  }, [item?.key, hasTertiary, currentPath, isMobile]);

  let tertiaryColumnSpan = 3;
  if (activeSecondary?.menuItemAcf?.promoImages?.length === 1) {
    tertiaryColumnSpan = 2;
  } else if (
    activeSecondary?.menuItemAcf?.promoImages?.length === 2
  ) {
    tertiaryColumnSpan = 1;
  }
  
  const makeSecondaryClickHandler = (child) => (e) => {
    e.preventDefault?.();

    if (isMobile) {
      setActiveSecondary(activeSecondary?.key === child.key ? null : child)

      return
    }

    // Desktop: if clicking the already-active item, go to its page
    if (activeSecondary?.key === child.key) {
      navigate(convertToRelativePath(child.path))

      return
    }

    // Otherwise, activate it (show its tertiary column)
    setActiveSecondary(child)
  }

  return (
    <MegaMenuStyles
      id={`mega-menu-${item.key}`}
      role="menu"
      aria-labelledby={`mega-menu-button-${item.key}`}
      $withTertiary={hasTertiary}
    >
      <Column>
        <SecondaryList $withTertiary={hasTertiary}>
          {item.children
            .filter((child) => child.title)
            .map((child) =>
              hasTertiary ? (
                <li key={child.key}>
                  <SecondaryItemButton
                    type="button"
                    onClick={makeSecondaryClickHandler(child)}
                    $active={activeSecondary?.key === child.key}
                    aria-expanded={activeSecondary?.key === child.key}
                  >
                    {child.menuItemAcf?.icon?.mediaItemUrl && (
                      <SafeSiteImage
                        image={child.menuItemAcf.icon.mediaItemUrl}
                        alt={child.title}
                        width="27"
                        height="27"
                      />
                    )}
                    {child.title}
                    {isMobile ? <span className="plus-icon">+</span> : <ChevronDownIcon />}
                  </SecondaryItemButton>
                  {isMobile &&
                    activeSecondary?.key === child.key &&
                    child.children?.length > 0 && (
                      <>
                        <TertiaryList $span={1}>
                          <TertiaryHeadingItem key={activeSecondary.key}>
                            <Link to={convertToRelativePath(activeSecondary.path)} role="menuitem">
                              {activeSecondary.title}
                            </Link>
                          </TertiaryHeadingItem>
                          {child.children
                            .filter((tertiary) => tertiary.title)
                            .map((tertiary) => (
                              <TertiaryItem key={tertiary.key}>
                                <Link
                                  to={convertToRelativePath(tertiary.path)}
                                  role="menuitem"
                                >
                                  {tertiary.title}
                                </Link>
                              </TertiaryItem>
                            ))}
                        </TertiaryList>
                        {activeSecondary?.menuItemAcf?.promoImages?.length > 0 &&
                          activeSecondary.menuItemAcf.promoImages
                            .filter(
                              (image) =>
                                image?.heading && image?.image?.mediaItemUrl,
                            )
                            .map((image, i) => (
                              <div key={`${image.heading}${i}`}>
                                {image.link?.url ? (
                                  <StyledImageLink to={convertToRelativePath(image.link.url)}>
                                    <SafeSiteImage
                                      image={image.image.mediaItemUrl}
                                      alt={image.heading}
                                      width={300}
                                    />
                                    <h2 dangerouslySetInnerHTML={{ __html: image.heading }} />
                                  </StyledImageLink>
                                ) : (
                                  <StyledImageSpan>
                                    <SafeSiteImage
                                      image={image.image.mediaItemUrl}
                                      alt={image.heading}
                                      width={300}
                                    />
                                    <h2 dangerouslySetInnerHTML={{ __html: image.heading }} />
                                  </StyledImageSpan>
                                )}
                              </div>
                            ))}
                      </>
                    )}
                </li>
              ) : (
                <SecondaryItem key={child.key}>
                  <Link to={convertToRelativePath(child.path)} role="menuitem">
                    {child?.menuItemAcf?.image?.mediaItemUrl && (
                      <div className="secondary-image-wrapper">
                        <SafeSiteImage
                          image={child.menuItemAcf.image.mediaItemUrl}
                          alt={child.title}
                        />
                      </div>
                    )}
                    <h2 dangerouslySetInnerHTML={{ __html: child.title }} />
                    {child?.menuItemAcf?.description && (
                      <div className="secondary-link-content">
                        <HtmlContent
                          htmlString={child.menuItemAcf.description}
                        />
                      </div>
                    )}
                  </Link>
                </SecondaryItem>
              ),
            )}
        </SecondaryList>
      </Column>

      {!isMobile && hasTertiary && activeSecondary && (
        <>
          <Column $span={tertiaryColumnSpan}>
            <TertiaryList $span={tertiaryColumnSpan}>
              <TertiaryHeadingItem key={activeSecondary.key}>
                <Link to={convertToRelativePath(activeSecondary.path)} role="menuitem">
                  {activeSecondary.title}
                </Link>
              </TertiaryHeadingItem>
              {activeSecondary.children.length > 0 &&
                activeSecondary.children
                  .filter((child) => child.title)
                  .map((tertiary) => (
                    <TertiaryItem key={tertiary.key}>
                      <Link to={convertToRelativePath(tertiary.path)} role="menuitem">
                        {tertiary.title}
                      </Link>
                    </TertiaryItem>
                  ))}
            </TertiaryList>
          </Column>

          {activeSecondary?.menuItemAcf?.promoImages?.length > 0 &&
            activeSecondary.menuItemAcf.promoImages
              .filter(
                (image) =>
                  image?.heading && image?.image?.mediaItemUrl,
              )
              .map((image, i) => (
                <Column key={`${image.heading}${i}`}>
                  {image.link?.url ? (
                    <StyledImageLink to={image.link.url}>
                      <SafeSiteImage
                        image={image.image.mediaItemUrl}
                        alt={image.heading}
                        width={300}
                      />
                      <h2 dangerouslySetInnerHTML={{ __html: image.heading }} />
                    </StyledImageLink>
                  ) : (
                    <StyledImageSpan>
                      <SafeSiteImage
                        image={image.image.mediaItemUrl}
                        alt={image.heading}
                        width={300}
                      />
                      <h2 dangerouslySetInnerHTML={{ __html: image.heading }} />
                    </StyledImageSpan>
                  )}
                </Column>
              ))}
        </>
      )}

      <CloseButton onClick={onClose} aria-label="Close">
        <CloseIcon />
      </CloseButton>
    </MegaMenuStyles>
  );
};

export default MegaMenu;
